import { create } from 'zustand'
import { apiClient } from '../lib/api'
import { track } from '../lib/analytics'
import type { MatchingTag, SearchResult, SearchResponse, TagWithCount } from '../lib/types'

interface SearchState {
  query: string
  results: SearchResult[]
  total: number
  typeFilter: string
  tagFilters: string[]
  popularTags: TagWithCount[]
  matchingTags: MatchingTag[]
  loading: boolean
  error: string | null
  offset: number

  setQuery: (query: string) => void
  search: (query: string) => Promise<void>
  setTypeFilter: (type: string) => void
  toggleTagFilter: (tag: string) => void
  loadMore: () => Promise<void>
  fetchPopularTags: () => Promise<void>
  clearSearch: () => void
}

export const useSearchStore = create<SearchState>((set, get) => ({
  query: '',
  results: [],
  total: 0,
  typeFilter: 'all',
  loading: false,
  error: null,
  tagFilters: [],
  popularTags: [],
  matchingTags: [],
  offset: 0,

  setQuery: (query) => set({ query }),

  search: async (query) => {
    if (!query.trim()) {
      set({ results: [], total: 0, offset: 0, matchingTags: [] })
      return
    }
    set({ loading: true, error: null, offset: 0 })
    try {
      const { typeFilter, tagFilters } = get()
      const params = new URLSearchParams({ q: query, type: typeFilter })
      if (tagFilters.length) params.set('tags', tagFilters.join(','))
      const data = await apiClient.get<SearchResponse>(`research/search/?${params}`)
      set({
        results: data.results,
        total: data.total,
        matchingTags: data.matching_tags || [],
      })
      track('search_performed', { query_length: query.length, result_count: data.total })
    } catch {
      set({ error: 'Search failed. Try again.' })
    } finally {
      set({ loading: false })
    }
  },

  setTypeFilter: (type) => {
    set({ typeFilter: type })
    const { query } = get()
    if (query) get().search(query)
  },

  toggleTagFilter: (tag) => {
    const { tagFilters, query } = get()
    const next = tagFilters.includes(tag)
      ? tagFilters.filter((t) => t !== tag)
      : [...tagFilters, tag]
    set({ tagFilters: next })
    if (query) get().search(query)
  },

  loadMore: async () => {
    const { query, typeFilter, tagFilters, results, total } = get()
    if (!query || results.length >= total) return
    set({ loading: true })
    try {
      const nextOffset = results.length
      const params = new URLSearchParams({
        q: query,
        type: typeFilter,
        offset: String(nextOffset),
      })
      if (tagFilters.length) params.set('tags', tagFilters.join(','))
      const data = await apiClient.get<SearchResponse>(`research/search/?${params}`)
      set((s) => ({
        results: [...s.results, ...data.results],
        total: data.total,
        offset: nextOffset,
      }))
    } catch {
      set({ error: 'Failed to load more results.' })
    } finally {
      set({ loading: false })
    }
  },

  fetchPopularTags: async () => {
    try {
      const data = await apiClient.get<TagWithCount[]>('research/tags/')
      set({ popularTags: data })
    } catch {
      // Tags are non-critical, fail silently
    }
  },

  clearSearch: () => {
    set({ query: '', results: [], total: 0, typeFilter: 'all', tagFilters: [], matchingTags: [], error: null, offset: 0 })
  },
}))
